import { Chat, WarningCircle } from '@phosphor-icons/react'
import Link from 'next/link'
import React, { useContext } from 'react'
import { createPortal } from 'react-dom'

import PowerUpPurchaseButton from '@/components/admin/powerups/PowerUpPurchaseButton'
import { Heading } from '@/components/shared/horizon'
import Button from '@/components/shared/ui/Button'
import {
  APP_NAME,
  MIGRATE_TO_PADDLE_CONTACT_SUPPORT_CONTENT,
} from '@/config/appConstants'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import { checkSourceIsStripe } from '@/lib/helpers/billingHelpers'
import { templateStringReplace } from '@/lib/helpers/stringHelpers'
import { openContactChat } from '@/lib/utils'

export default function MigrateToPaddleAlert() {
  const { organizationPlan, userProfile } = useContext(HNContext)
  const t = useTranslations('billing')

  const renderRightComponent = () => {
    if (organizationPlan?.can_migrate_to_paddle) {
      return (
        <PowerUpPurchaseButton
          variant='primary'
          migrate={true}
          disabled={false}
          loading={false}
          powerUps={null}
          interval={undefined}
          onClose={() => {}}
          buttonProps={{
            variant: 'primary',
            size: 'md',
            rounded: 'full',
          }}
        />
      )
    }
    return null
  }
  if (!organizationPlan?.show_migrate_banner) return null

  if (!organizationPlan) return null

  if (!userProfile?.is_csm_of_organization) return null

  if (!organizationPlan?.active_plan_subscription) return null

  if (organizationPlan?.source === 'paddle') return null

  if (
    !checkSourceIsStripe({
      plan_info: organizationPlan,
    })
  )
    return null

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className='absolute bottom-0 top-0 z-10 flex w-full flex-col items-center justify-center backdrop-blur'>
      <div className='mx-auto max-w-2xl rounded-xl border border-gray10 bg-gray1 shadow'>
        <div className='mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-4 px-4 py-4'>
          <Heading>We need your support 🙌</Heading>
          {organizationPlan?.can_migrate_to_paddle ? (
            <p className='text-center text-base font-medium text-gray11'>
              Our current Stripe account support has been deprecated, and we
              have transitioned to Paddle for a more efficient billing process!
              Please migrate your subscription to paddle to continue using the
              service.
            </p>
          ) : (
            <>
              <p className='text-center text-base font-medium text-gray11'>
                Our current Stripe account support has been deprecated, and we
                have transitioned to Paddle for a more efficient billing
                process!
              </p>
              <p className='text-center text-base font-medium text-gray11'>
                Since you have either active power-up(s) or coupon tied to your
                subscription, you will need to contact support to migrate your
                subscription to paddle.
              </p>
            </>
          )}

          <p className='text-center text-base font-medium text-gray11'>
            Upon migration, your current pricing and plan will also be carried
            over.
          </p>
          <Link
            href='https://help.featureos.app/articles/why-migrating-to-paddle-is-mandatory'
            className='text-blue10 underline'
            target='_blank'
          >
            Read more about the necessity of this change &rarr;
          </Link>
          {userProfile?.is_admin_of_organization ? (
            <div className='flex items-center gap-2'>
              {renderRightComponent()}
              <Button
                size='md'
                variant='primary'
                rounded='full'
                icon={<Chat size={18} />}
                onClick={() =>
                  openContactChat(
                    templateStringReplace(
                      MIGRATE_TO_PADDLE_CONTACT_SUPPORT_CONTENT,
                      {
                        appName: APP_NAME,
                      }
                    )
                  )
                }
              >
                {t('common.contactSupport', { ignorePrefix: true })}
              </Button>
            </div>
          ) : (
            <div>
              <p className='text-base'>
                Please contact your administrator regarding this.
              </p>
            </div>
          )}
          <p className='inline-flex items-center gap-1 text-gray10'>
            <WarningCircle />
            Note that this banner is only visible to your team members and not
            your customers.
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}
